import { api } from 'services/api';

import { AddScannerFormData, Gate, PartnerSettings } from './types';

/**
 * Green-owned clone of the dark `features/GatesManagement/services.ts` data layer. The calls are
 * zone-neutral (axios via `services/api` — Bearer token + dev users-api base-URL rewrite handled by
 * the shared interceptor); they are cloned (not imported) so the green Gates feature stays
 * self-contained for the downstroke lift. Contracts are server-verified against ticketing-api-2.0
 * (gates/assign) + users-api-2.0 (members/invite).
 */

export const getEventGates = async (eventId: string): Promise<Gate[]> => {
	const response = await api.get(`/events/${eventId}/gates`);
	return response.data;
};

export const getPartnerMembers = async (): Promise<PartnerSettings> => {
	const response = await api.get('/partners/settings');
	return response.data;
};

export const createEventGate = async (eventId: string, name: string): Promise<Gate> => {
	const response = await api.post(`/events/${eventId}/gates`, { name });
	return response.data;
};

export const deleteEventGate = async (eventId: string, gateId: number): Promise<void> => {
	await api.delete(`/events/${eventId}/gates/${gateId}`);
};

export const assignScanner = async (eventId: string, gateId: number, scannerId: number): Promise<void> => {
	await api.post(`/events/${eventId}/gates/${gateId}/assign`, { scanner_id: scannerId });
};

export const removeScanner = async (eventId: string, gateId: number, scannerId: number): Promise<void> => {
	await api.delete(`/events/${eventId}/gates/${gateId}/unassign/${scannerId}`);
};

/**
 * Invite a new partner member as a scanner. The users-api `CreateMemberRequestModel` reads
 * `roles: string[]` (plural) — the dark code sent singular `role`, which the server silently
 * dropped, leaving the invitee with NO role. Green sends the correct `roles: ['scanner']`.
 * The endpoint is admin-only, so the calling UI must gate the invite action by role.
 */
export const inviteScanner = async (data: AddScannerFormData): Promise<void> => {
	await api.post('/members/invite', { ...data, roles: ['scanner'] });
};
